import { useCallback, useState } from 'react'

import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { useDeletePODisclaimerLabelPresenter } from '@webapp/modules/purchasing/features/delete-po-disclaimer-label/presenters/UseDeletePODisclaimerLabelPresenter'
import { useGetDefaultPODisclaimerPresenter } from '@webapp/modules/purchasing/features/get-default-po-disclaimer/presenters/UseGetDefaultPODisclaimerPresenter'
import { usePODisclaimerLabelListPresenter } from '@webapp/modules/purchasing/features/list-po-disclaimer-labels/presenters/PODisclaimerLabelListPresenter'
import { useSetDefaultPODisclaimerPresenter } from '@webapp/modules/purchasing/features/set-default-po-disclaimer/presenters/UseSetDefaultPODisclaimerPresenter'

/**
 * Orchestration hook for PODisclaimerLabelsView.
 *
 * Owns the create-modal lifecycle (BHV-019/021) and bridges the default-star
 * state across three features (read hook → list prop → toggle command,
 * BHV-017b). Guard data for the create uniqueness check comes from the list
 * feature's presenter companion hook, which React Query dedupes with the
 * rendered presenter's query (Feature Set Companion Hook Pattern).
 */
export const usePODisclaimerLabelsView = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  // Headless companion use — guard data for the create uniqueness check
  const {
    data: { labels },
  } = usePODisclaimerLabelListPresenter()

  const { defaultId } = useGetDefaultPODisclaimerPresenter()

  const {
    commands: { toggleDefault },
  } = useSetDefaultPODisclaimerPresenter()

  const {
    commands: { deleteLabel },
  } = useDeletePODisclaimerLabelPresenter()

  const onToggleDefault = (label: PODisclaimerLabel) => {
    // The FS supplies currentDefaultId so the command knows set-vs-clear
    toggleDefault(label, defaultId)
  }

  const onDeleteLabel = (label: PODisclaimerLabel) => {
    deleteLabel(label)
  }

  return {
    data: {
      labels,
      defaultId,
    },
    states: {
      isCreateModalOpen,
    },
    methods: {
      openCreateModal,
      closeCreateModal,
      onToggleDefault,
      onDeleteLabel,
    },
  }
}
